import React, { useCallback, useEffect, useState } from "react"
import {
  AppBar,
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material"
import { Link, useLocation } from "react-router-dom"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import MenuIcon from "@mui/icons-material/Menu"
import CloseIcon from "@mui/icons-material/Close"
import { useSelector } from "react-redux"
import Axios from "../../Util/Axios"

/* ── Anchor links for the public single-page site ── */
const PUBLIC_NAV_ITEMS = [
  { name: "About", anchor: "about" },
  { name: "Gallery", anchor: "gallery" },
  { name: "FAQ", anchor: "faq" },
  { name: "Contact", anchor: "contact" },
]

const NavBar = ({ scrolling = false }) => {
  const location = useLocation()
  const isHome = location.pathname === "/"

  const [mobileOpen, setMobileOpen] = useState(false)
  const [solid, setSolid] = useState(!scrolling || !isHome)
  const [customOrderAnchorEl, setCustomOrderAnchorEl] = useState(null)
  const [displayPresets, setDisplayPresets] = useState([])

  const { cart } = useSelector((state) => state)
  const cartItems = cart.cart_items || []

  /* ── Fetch presets for the Custom Order dropdown ── */
  useEffect(() => {
    Axios.get("/api/preset-list/?type=display")
      .then(({ data }) => setDisplayPresets(data.results))
      .catch(() => {})
  }, [])

  /* ── Transparent → solid on scroll (only when scrolling=true and on /) ── */
  useEffect(() => {
    if (!scrolling || !isHome) {
      setSolid(true)
      return
    }

    const onScroll = () => {
      setSolid(window.scrollY > 60)
    }

    setSolid(window.scrollY > 60)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [scrolling, isHome])

  const scrollTo = useCallback((id) => {
    if (!isHome) {
      window.location.href = `/#${id}`
      return
    }
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: "smooth" })
    setMobileOpen(false)
  }, [isHome])

  const handleDrawerToggle = () => setMobileOpen((v) => !v)
  const handlePresetMenuClose = () => setCustomOrderAnchorEl(null)

  /* ── Styles ── */
  const navBg = solid
    ? "rgba(13, 27, 42, 0.97)"
    : "transparent"

  const backdropFilter = solid ? "blur(8px)" : "none"

  /* ── Mobile drawer ── */
  const drawer = (
    <Box
      sx={{
        width: 280,
        height: "100%",
        backgroundColor: "#0d1b2a",
        color: "white",
        display: "flex",
        flexDirection: "column",
        pt: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          pb: 3,
          borderBottom: "1px solid rgba(200,185,154,0.1)",
          mb: 2,
        }}
      >
        <Typography
          component={Link}
          to="/"
          onClick={handleDrawerToggle}
          sx={{
            fontFamily: "Kenilla, Lobster, serif",
            fontSize: "1.6rem",
            color: "white",
            textDecoration: "none",
            letterSpacing: "0.1em",
          }}
        >
          MARLIN
        </Typography>
        <IconButton color="inherit" onClick={handleDrawerToggle} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <List disablePadding>
        {/* Custom Order link */}
        <ListItem
          button
          component={Link}
          to="/custom-surfboard-order"
          onClick={handleDrawerToggle}
          sx={{ px: 3, py: 1.5 }}
        >
          <ListItemText
            primary="Custom Order"
            primaryTypographyProps={{
              sx: {
                fontFamily: "Josefin Sans, sans-serif",
                fontSize: "0.75rem",
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "#c8b99a",
              },
            }}
          />
        </ListItem>

        {/* Anchor links */}
        {PUBLIC_NAV_ITEMS.map((item) => (
          <ListItem
            button
            key={item.anchor}
            onClick={() => scrollTo(item.anchor)}
            sx={{ px: 3, py: 1.5 }}
          >
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                sx: {
                  fontFamily: "Josefin Sans, sans-serif",
                  fontSize: "0.75rem",
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  color: "rgba(232,228,220,0.75)",
                },
              }}
            />
          </ListItem>
        ))}
      </List>

      <Box sx={{ mt: "auto", px: 3, py: 3, borderTop: "1px solid rgba(200,185,154,0.1)" }}>
        <Typography
          sx={{
            fontFamily: "Josefin Sans, sans-serif",
            fontSize: "0.7rem",
            letterSpacing: "0.15em",
            color: "rgba(232,228,220,0.3)",
          }}
        >
          @marlin.surf
        </Typography>
      </Box>
    </Box>
  )

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: navBg,
        backdropFilter,
        WebkitBackdropFilter: backdropFilter,
        boxShadow: solid
          ? "0 1px 0 rgba(200,185,154,0.1)"
          : "none",
        transition:
          "background 0.4s ease, box-shadow 0.4s ease, backdrop-filter 0.4s ease",
        zIndex: 1200,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 2, md: 4 },
          background: "transparent",
        }}
      >
        {/* ── Mobile hamburger ── */}
        <IconButton
          color="inherit"
          aria-label="open menu"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 1, display: { xs: "flex", md: "none" } }}
          size="small"
        >
          <MenuIcon />
        </IconButton>

        {/* ── Mobile drawer ── */}
        <Drawer
          variant="temporary"
          anchor="left"
          PaperProps={{ sx: { backgroundColor: "transparent" } }}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
        >
          {drawer}
        </Drawer>

        {/* ── Logo ── */}
        <IconButton
          edge="start"
          color="inherit"
          component={Link}
          to="/"
          sx={{ mr: 1, p: 0.5 }}
          disableRipple
        >
          <img
            height={28}
            width={46}
            src="https://marlinsurf-assets.s3.us-west-1.amazonaws.com/marlinmedium.png"
            alt="Marlin logo"
          />
        </IconButton>

        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            fontFamily: "Kenilla, Lobster, serif",
            fontSize: { xs: "1.2rem", sm: "1.4rem" },
            letterSpacing: "0.12em",
            color: "white",
            textDecoration: "none",
            textShadow: solid ? "none" : "0 2px 12px rgba(0,0,0,0.5)",
            transition: "text-shadow 0.4s ease",
          }}
        >
          MARLIN
        </Typography>

        {/* ── Desktop nav links ── */}
        <Box
          component="nav"
          sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5 }}
        >
          {/* Custom Order (dropdown) */}
          <Typography
            component="button"
            onClick={(e) => setCustomOrderAnchorEl(e.currentTarget)}
            sx={{
              fontFamily: "Josefin Sans, sans-serif",
              fontSize: "0.65rem",
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: "rgba(232,228,220,0.75)",
              background: "none",
              border: "none",
              cursor: "pointer",
              px: 2,
              py: 1,
              transition: "color 0.2s ease",
              "&:hover": { color: "#c8b99a" },
            }}
          >
            Custom Order
          </Typography>

          <Menu
            anchorEl={customOrderAnchorEl}
            open={Boolean(customOrderAnchorEl)}
            onClose={handlePresetMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            PaperProps={{
              sx: {
                backgroundColor: "#0d1b2a",
                border: "1px solid rgba(200,185,154,0.15)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                borderRadius: 0,
                mt: 1,
              },
            }}
          >
            {displayPresets.map((preset) => (
              <MenuItem
                key={preset.id}
                sx={{
                  fontFamily: "Josefin Sans, sans-serif",
                  color: "rgba(232,228,220,0.75)",
                  fontSize: "0.8rem",
                  letterSpacing: "0.1em",
                  px: 4,
                  "&:hover": { color: "#c8b99a", backgroundColor: "rgba(200,185,154,0.06)" },
                }}
                component={Link}
                to={`/custom-surfboard-order?preset_id=${preset.id}`}
                onClick={handlePresetMenuClose}
              >
                {preset.name}
              </MenuItem>
            ))}
            <MenuItem
              sx={{
                fontFamily: "Josefin Sans, sans-serif",
                color: "rgba(232,228,220,0.75)",
                fontSize: "0.8rem",
                letterSpacing: "0.1em",
                px: 4,
                "&:hover": { color: "#c8b99a", backgroundColor: "rgba(200,185,154,0.06)" },
              }}
              component={Link}
              to="/custom-surfboard-order"
              onClick={handlePresetMenuClose}
            >
              Start from scratch
            </MenuItem>
          </Menu>

          {/* Anchor links */}
          {PUBLIC_NAV_ITEMS.map((item) => (
            <Typography
              key={item.anchor}
              component="button"
              onClick={() => scrollTo(item.anchor)}
              sx={{
                fontFamily: "Josefin Sans, sans-serif",
                fontSize: "0.65rem",
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "rgba(232,228,220,0.75)",
                background: "none",
                border: "none",
                cursor: "pointer",
                px: 2,
                py: 1,
                transition: "color 0.2s ease",
                "&:hover": { color: "#c8b99a" },
              }}
            >
              {item.name}
            </Typography>
          ))}
        </Box>

        {/* ── Cart icon ── */}
        <IconButton
          size="small"
          edge="end"
          color="inherit"
          component={Link}
          to="/cart"
          aria-label="Shopping Cart"
          sx={{ ml: 1 }}
        >
          {cartItems.length > 0 ? (
            <Badge badgeContent={cartItems.length} color="secondary">
              <ShoppingCartIcon fontSize="small" />
            </Badge>
          ) : (
            <ShoppingCartIcon fontSize="small" />
          )}
        </IconButton>
      </Toolbar>
    </AppBar>
  )
}

export default NavBar
